import { useMemo, useState } from 'react';
import { Box, Snackbar, Alert } from '@mui/material';
import { TableTitleBox, PaginationTable, LoadingSkeleton } from '../../common/components';
import DebtNotificationsFilters from './components/DebtNotificationsFilters';
import DebtNotificationsSummary from './components/DebtNotificationsSummary';
import DebtNotificationsInfoBar from './components/DebtNotificationsInfoBar';
import DebtNotificationsDataTable from './components/DebtNotificationsDataTable';
import NotificationsSendDialog from './components/NotificationsSendDialog';
import DebtEmailResultsDialog from '../common/components/DebtEmailResultsDialog';
import { useDebtNotificationsTable } from './hooks/useDebtNotificationsTable';
import { normalizeDebtEmailResults } from '../common/utils/emailResultUtils';

const DebtNotificationsTable = () => {
  const {
    filter,
    page,
    totalPages,
    totalCount,
    rowsPerPage,
    data,
    summary,
    isLoading,
    isFetching,
    selectedMap,
    selectedSummary,
    notificationResults,
    resultsDialogOpen,
    openResultsDialog,
    closeResultsDialog,
    isSelectingAll,
    selectAllNotifications,
    handleDateChange,
    handleDatePreset,
    handleResetFilters,
    handlePageChange,
    handleRowsPerPageChange,
    toggleRowSelection,
    isRowFullySelected,
    clearSelection,
    buildSendPayload,
    sendDebtNotificationsMutation,
  } = useDebtNotificationsTable();

  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleCloseSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  const selectedEntries = useMemo(
    () =>
      Object.values(selectedMap).map((entry) => ({
        header: entry.header,
        coupons: Object.values(entry.coupons || {}),
      })),
    [selectedMap]
  );

  const handleConfirmSend = async (message) => {
    const payload = buildSendPayload(message);
    try {
      const result = await sendDebtNotificationsMutation.mutateAsync(payload);
      const normalized = normalizeDebtEmailResults(result, { payload });
      openResultsDialog(normalized);

      const hasFailures = (normalized.summary?.failed_count ?? 0) > 0;
      setSnackbar({
        open: true,
        message: normalized.message,
        severity: hasFailures ? 'warning' : 'success',
      });

      clearSelection();
      setSendDialogOpen(false);
    } catch (error) {
      console.error(error);
      const apiResult = error?.response?.data;
      const errorMessage =
        apiResult?.message ||
        apiResult?.error ||
        error?.message ||
        'Ocurrió un error al enviar las notificaciones.';

      const fallbackResult = {
        message: errorMessage,
        failed_details: selectedEntries.map((entry) => ({
          contractId: entry.header?.contract_number,
          subscriber: entry.header?.subscriber,
          email: entry.header?.subscriber_email,
          error: errorMessage,
        })),
      };

      const normalized = normalizeDebtEmailResults(apiResult || fallbackResult, { payload });
      openResultsDialog(normalized);

      setSnackbar({
        open: true,
        message: normalized.message,
        severity: 'error',
      });

      clearSelection();
      setSendDialogOpen(false);
    }
  };

  return (
    <Box>
      <TableTitleBox title="Habitat 99 | Mora | Notificaciones por Vencer" />

      <DebtNotificationsFilters
        filter={filter}
        onDateChange={handleDateChange}
        onDatePreset={handleDatePreset}
        onResetFilters={handleResetFilters}
      />

      <DebtNotificationsSummary
        summary={summary}
        selectedSummary={selectedSummary}
        onClearSelection={clearSelection}
        onSendNotifications={() => setSendDialogOpen(true)}
        disableClear={selectedSummary.selectedCoupons === 0}
        disableSend={selectedSummary.selectedCoupons === 0}
      />

      <DebtNotificationsInfoBar
        summary={summary}
        currentCount={data.length}
        onSelectAll={selectAllNotifications}
        disableSelectAll={
          (summary?.total_records ?? totalCount) === 0 ||
          isSelectingAll ||
          selectedEntries.length >= (summary?.total_records ?? totalCount)
        }
        isSelectingAll={isSelectingAll}
      />

      <DebtNotificationsDataTable
        data={data}
        isLoading={isLoading || isFetching}
        onToggleRow={toggleRowSelection}
        isRowFullySelected={isRowFullySelected}
      />

      {totalCount > rowsPerPage && (
        <PaginationTable
          totalPages={totalPages}
          totalCount={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      )}

      <NotificationsSendDialog
        open={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
        onConfirm={handleConfirmSend}
        selectedEntries={selectedEntries}
        isSubmitting={sendDebtNotificationsMutation.isPending}
      />

      <DebtEmailResultsDialog
        open={resultsDialogOpen}
        onClose={closeResultsDialog}
        results={notificationResults}
        title="Resultado del envío de recordatorios por vencer"
        successSectionLabel="Correos enviados"
        errorSectionLabel="Correos con error"
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DebtNotificationsTable;
